/** Notifications → Overview: the guest-lifecycle timeline for one tenant.
 *
 *  This page owns everything the component deliberately does not — the tenant
 *  picker, the two queries, the mutations, and the URL state. `NotificationTimeline`
 *  only ever sees a built model.
 *
 *  Two queries on purpose: the timeline paints as soon as the effective set
 *  arrives, and the 30-day fire counts layer in when the (replica, cached)
 *  aggregate returns. A slow stats query never delays the overview.
 */
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as timelineApi from '@/api/notificationTimeline';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { usePermissions } from '@/hooks/usePermissions';
import { notificationsHref } from '@/utils/gridLinks';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { CreateNotificationDialog, SYSTEM_TENANT } from '../notifications/CreateNotificationDialog';
import { anatomyUrl } from '../notification-anatomy/anatomyLink';
import { deliveryLogsUrl } from '../notifications/deliveryLogsLink';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { adaptCatalog, adaptStats, adaptTimeline, applyStatsWarnings } from './model/adapt';
import { buildTimeline, familiesIn } from './model/buildTimeline';
import type { Density, SlotRef, TimelineActions } from './model/types';
import { NotificationTimeline, timelineCounts } from './NotificationTimeline';
import { TimelineToolbar } from './TimelineToolbar';

/** `system` is a real selection here (the tour_operator IS NULL rows), so the
 *  picker's sentinel maps to the string the API expects — and to the same
 *  string in the URL. */
function toParam(tenant: TenantPickerValue | null): string | null {
  if (!tenant) return null;
  return tenant.id === SYSTEM_TENANT.id ? 'system' : String(tenant.id);
}

function fromParam(raw: string | null): timelineApi.TimelineTenantParam | null {
  if (raw === 'system') return 'system';
  if (raw && /^\d+$/.test(raw)) return Number(raw);
  return null;
}

export function NotificationTimelinePage() {
  const [params, setParams] = useSearchParams();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_notifications_manage');

  // The picker only needs a display name; the tenant IDENTITY lives in the URL
  // so the view is linkable and survives a reload.
  const [pickedTenant, setPickedTenant] = useState<TenantPickerValue | null>(null);
  const [createFor, setCreateFor] = useState<SlotRef | null>(null);
  const [busyNotifications, setBusyNotifications] = useState<ReadonlySet<number>>(new Set());
  const [busyMethods, setBusyMethods] = useState<ReadonlySet<number>>(new Set());
  const [busyEvents, setBusyEvents] = useState<ReadonlySet<string>>(new Set());

  // --- URL state ------------------------------------------------------------
  // `balanced` accepted for links shared before the rename.
  const densityParam = params.get('density');
  const density: Density =
    densityParam === 'detailed' || densityParam === 'balanced' ? 'detailed' : 'compact';
  const selectedFamilies = (params.get('families') ?? '').split(',').filter(Boolean);
  const search = params.get('q') ?? '';
  const showEmpty = params.get('empty') === '1';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // URL ?tenant= (id or the 'system' sentinel) over the console-wide scope.
  const pageTenant = usePageTenant();
  const tenantId = fromParam(pageTenant.raw);

  // --- data -----------------------------------------------------------------
  // The catalog is static per deploy — fetched once, never refetched, and it is
  // what lets the timeline render moments that have nothing configured.
  const catalog = useQuery({
    queryKey: ['notification-event-catalog'],
    queryFn: timelineApi.getEventCatalog,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const timeline = useQuery({
    queryKey: ['notification-timeline', tenantId],
    queryFn: () => timelineApi.getTimeline(tenantId!),
    enabled: tenantId !== null,
  });

  const stats = useQuery({
    queryKey: ['notification-timeline-stats', tenantId],
    queryFn: () => timelineApi.getTimelineStats(tenantId!),
    enabled: tenantId !== null && timeline.data?.stats_available === true,
    staleTime: 5 * 60_000,
  });

  /** What the picker shows. A URL with only an id starts as a placeholder and
   *  fills in from the timeline payload — no extra lookup request. */
  const tenantValue = useMemo<TenantPickerValue | null>(() => {
    if (tenantId === null) return null;
    if (tenantId === 'system') return SYSTEM_TENANT;
    if (pickedTenant && pickedTenant.id === tenantId) return pickedTenant;
    const loaded = timeline.data?.tenant;
    if (loaded && loaded.id === tenantId) {
      return { id: loaded.id, name: loaded.name, code: loaded.code };
    }
    return { id: tenantId, name: `#${tenantId}` };
  }, [tenantId, pickedTenant, timeline.data]);

  const onTenantChange = useCallback(
    (next: TenantPickerValue | null) => {
      setPickedTenant(next);
      pageTenant.setRaw(toParam(next));
    },
    [pageTenant],
  );

  const adaptedStats = useMemo(
    () => (stats.data ? adaptStats(stats.data) : undefined),
    [stats.data],
  );

  const model = useMemo(() => {
    if (!timeline.data) return null;
    const adapted = adaptTimeline(timeline.data);
    let notifications = applyStatsWarnings(adapted.notifications, adaptedStats);
    const needle = search.trim().toLowerCase();
    if (needle) {
      notifications = notifications.filter(
        (n) =>
          n.name.toLowerCase().includes(needle) || n.event.toLowerCase().includes(needle),
      );
    }
    return buildTimeline(notifications, {
      // ALWAYS pass the catalog: it is what resolves an event code to its human
      // label. Passing it only when empty events were shown made the toggle
      // rename every event (label ⇄ raw code). What renders is decided by
      // `hideEmpty` alone — a search narrows the question to "where are these",
      // so unused events are suppressed then regardless of the switch.
      catalog: adaptCatalog(catalog.data ?? []),
      families: selectedFamilies,
      tenant: adapted.tenant,
      generatedTs: adapted.generatedTs,
      statsAvailable: adapted.statsAvailable,
      hideEmpty: !showEmpty || Boolean(needle),
      // 4. Itinerary beats are not in use; the band would be dead weight.
      hiddenBands: ['itinerary'],
    });
  }, [timeline.data, adaptedStats, catalog.data, selectedFamilies, search, showEmpty]);

  const allFamilies = useMemo(() => {
    if (!timeline.data) return [];
    const full = buildTimeline(adaptTimeline(timeline.data).notifications);
    return familiesIn(full);
  }, [timeline.data]);

  // --- mutations ------------------------------------------------------------
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['notification-timeline', tenantId] });

  const withBusy = async <T,>(
    set: React.Dispatch<React.SetStateAction<ReadonlySet<T>>>,
    key: T,
    run: () => Promise<unknown>,
  ) => {
    set((prev) => new Set(prev).add(key));
    try {
      await run();
      await invalidate();
    } catch (error) {
      snackbar.show(
        error instanceof ConsoleApiError ? error.message : 'The change could not be applied.',
        'error',
      );
    } finally {
      set((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const toggleNotification = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      timelineApi.setNotificationEnabled(id, enabled),
  });

  const toggleMethod = useMutation({
    mutationFn: (vars: { notificationId: number; dmId: number; enabled: boolean }) =>
      timelineApi.toggleDeliveryMethod(vars),
  });

  const toggleEventMutation = useMutation({
    mutationFn: (vars: { event: string; enabled: boolean }) =>
      timelineApi.toggleEvent({ tenant: tenantId!, ...vars }),
  });

  const actions: TimelineActions | undefined = useMemo(() => {
    if (!canManage) {
      // Read-only still gets the deep links — they are the useful half for a
      // viewer, and the backend would reject the writes anyway.
      return {
        notificationHref: (id) => `/notifications/${id}`,
        deliveryLogsHref: (id) => deliveryLogsUrl(id),
        anatomyHref: (id) => anatomyUrl(id, tenantId ?? undefined),
        tourHref: (id) => `/tours/${id}`,
      };
    }
    return {
      onToggleNotification: (id, enabled) =>
        withBusy(setBusyNotifications, id, () =>
          toggleNotification.mutateAsync({ id, enabled }),
        ),
      onToggleDeliveryMethod: (notificationId, dmId, enabled) =>
        withBusy(setBusyMethods, dmId, () =>
          toggleMethod.mutateAsync({ notificationId, dmId, enabled }),
        ),
      onToggleEvent: (event, enabled) =>
        withBusy(setBusyEvents, event, async () => {
          const result = await toggleEventMutation.mutateAsync({ event, enabled });
          if (result.skipped.length) {
            // Silent skips would read as a failed click. Name the reason.
            const reasons = [...new Set(result.skipped.map((s) => s.reason))].join(' / ');
            snackbar.show(
              `${result.updated.length} updated · ${result.skipped.length} left alone (${reasons} — edit those on their owner)`,
              'info',
            );
          }
        }),
      onCreateNotification: (slot) => setCreateFor(slot),
      notificationHref: (id) => `/notifications/${id}`,
      deliveryLogsHref: (id) => deliveryLogsUrl(id),
      // The timeline already knows which tenant is on screen, so the anatomy
      // page opens with its picker narrowed to the same one.
      anatomyHref: (id) => anatomyUrl(id, tenantId ?? undefined),
      tourHref: (id) => `/tours/${id}`,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, tenantId]);

  const counts = model ? timelineCounts(model) : { notifications: 0, live: 0 };

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        title="Notification overview"
        subtitle="Everything this tenant sends, in the order a guest receives it."
        helpStory="notification-timeline"
      />

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            value={tenantValue}
            onChange={onTenantChange}
            extraOptions={[SYSTEM_TENANT]}
          />
          {/* The picker owns its own click; the operator behind it is reachable
              from the caption underneath. */}
          {typeof tenantId === 'number' && model?.tenant && (
            <PickerLinks
              items={[
                {
                  key: model.tenant.id,
                  label: model.tenant.code || model.tenant.name,
                  actions: operatorActions(model.tenant, {
                    href: notificationsHref([], model.tenant.id),
                    label: 'Notifications of this operator',
                  }),
                },
              ]}
            />
          )}
        </Box>
        {timeline.isFetching && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        {model?.statsAvailable === false && (
          <Typography variant="caption" color="text.secondary">
            Send counts unavailable in this environment
          </Typography>
        )}
      </Stack>

      {tenantId === null ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Pick a tenant to see its notification timeline.
          </Typography>
        </Paper>
      ) : timeline.isError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => timeline.refetch()}>
              Retry
            </Button>
          }
        >
          {timeline.error instanceof ConsoleApiError
            ? timeline.error.message
            : 'Couldn’t load the timeline.'}
        </Alert>
      ) : timeline.isPending || !model ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', p: 3 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading…</Typography>
        </Stack>
      ) : (
        <>
          <TimelineToolbar
            density={density}
            onDensityChange={(d) => setParam('density', d === 'compact' ? null : d)}
            families={allFamilies}
            selectedFamilies={selectedFamilies}
            onFamiliesChange={(f) => setParam('families', f.join(','))}
            search={search}
            onSearchChange={(v) => setParam('q', v)}
            showEmpty={showEmpty}
            onShowEmptyChange={(v) => setParam('empty', v ? '1' : null)}
            counts={counts}
          />

          <NotificationTimeline
            model={model}
            density={density}
            stats={adaptedStats}
            actions={actions}
            busyNotificationIds={busyNotifications}
            busyMethodIds={busyMethods}
            busyEvents={busyEvents}
            emptyState={
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography color="text.secondary">
                  {search
                    ? 'No notification matches this filter.'
                    : 'This tenant has no notifications yet.'}
                </Typography>
              </Paper>
            }
          />
        </>
      )}

      {createFor && (
        <CreateNotificationDialog
          open
          onClose={() => setCreateFor(null)}
          defaultEvent={createFor.eventCode}
          // The page's operator (D9), named by the pick or the timeline payload —
          // never by the picker's '#id' placeholder; `system` opens on System.
          pageTenants={pageTenantsFrom(typeof tenantId === 'number' ? String(tenantId) : null, [
            pickedTenant,
            timeline.data?.tenant,
          ])}
          defaultSystem={tenantId === 'system'}
        />
      )}
    </Box>
  );
}
